<template>
  <div class="flex flex-wrap px-4 pb-10 mt-10 sm:px-6 lg:px-32">
    <div class="w-full text-white lg:w-1/3">
      <route-discovery
        :class="isScheduleBooking ? 'disable-form' : ''"
        class="
          px-4
          py-6
          border
          rounded-md
          lg:max-w-sm-5
          bg-secondary
          border-dark-type-2
        "
        @discovered="setRoutes"
        @discovering="
          discovering = true;
          resetSelectedOptions();
        "
      >
        <template v-slot:title>
          <div class="text-2xl font-bold" v-if="user">
            Hello {{ user.fname }},
          </div>
          <div class="font-bold pr-14">Discover shuttles that go your way</div>
        </template>
      </route-discovery>
    </div>
    <div class="w-full mt-8 ml-6 lg:w-1/3">
      <div class="text-2xl font-bold text-dark-type-3">
        {{ isScheduleBooking ? "Schedule your booking" : "Quick bookings" }}
      </div>
      <p
        class="font-medium text-xs text-red-type-1 cursor-pointer"
        @click="resetSelectedOptions"
        v-if="isScheduleBooking"
      >
        Cancel schedule
      </p>
      <div class="mt-2 text-sm font-medium text-dark-type-4">
        Pick from the shuttles on the road today
      </div>
      <template v-if="discovering">
        <div
          class="flex items-center justify-center h-full mt-5 bg-gray-type-4"
        >
          <div class="my-40 spinner"></div>
        </div>
      </template>
      <template v-else>
        <template v-if="discovered">
          <template v-if="routes.discoveredRoutes.first_trip_routes.length > 0">
            <div class="mt-5">
              <div class="px-4 py-4 bg-gray-type-4">
                <div
                  v-if="!is_favorite_trip"
                  class="text-sm font-bold text-dark-type-3"
                >
                  Choose a first trip
                </div>
                <div v-else class="text-sm font-bold text-dark-type-3">
                  Choose from favorite trips
                </div>
              </div>
              <div class="bg-white">
                <div
                  style="position: relative"
                  v-for="(route, index) in routes.discoveredRoutes
                    .first_trip_routes"
                  :key="index"
                  class="block pt-4 border-b border-gray-type-2"
                  @click="selectFirstRoute(route)"
                >
                  <route-information
                    :route="route"
                    :selected-route-id="data.first_route_id"
                  />
                </div>
              </div>
            </div>
            <template
              v-if="routes.discoveredRoutes.first_trip_routes.length > 0"
            >
              <div v-show="isReturnTrip">
                <div class="px-4 py-4 bg-gray-type-4">
                  <div class="text-sm font-bold text-dark-type-3">
                    Choose a second trip
                  </div>
                </div>
                <template
                  v-if="routes.discoveredRoutes.second_trip_routes.length > 0"
                >
                  <div class="bg-white">
                    <div
                      style="position: relative"
                      v-for="(route, index) in routes.discoveredRoutes
                        .second_trip_routes"
                      :key="index"
                      class="block pt-4 border-b border-gray-type-2"
                      @click="selectSecondRoute(route)"
                    >
                      <route-information
                        :route="route"
                        :selected-route-id="data.second_route_id"
                      />
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div
                    class="
                      flex flex-col
                      items-center
                      justify-center
                      h-full
                      px-4
                      py-10
                    "
                  >
                    <div class="font-medium text-center">
                      We could not find any available buses for this route.
                    </div>
                    <button
                      @click.prevent="processRouteSuggestion()"
                      class="
                        h-12
                        px-4
                        py-4
                        mt-6
                        font-bold
                        text-center text-white
                        rounded
                        bg-active
                      "
                    >
                      Suggest this route to us
                    </button>
                  </div>
                </template>
              </div>
            </template>
          </template>
          <template v-else>
            <div
              class="
                flex flex-col
                items-center
                justify-center
                h-full
                px-4
                mt-5
                bg-gray-type-4
              "
            >
              <div class="font-medium text-center">
                We could not find any available buses for this route.
              </div>
              <button
                @click.prevent="processRouteSuggestion()"
                class="
                  h-12
                  px-4
                  py-4
                  mt-6
                  font-bold
                  text-center text-white
                  rounded
                  bg-active
                "
              >
                Suggest this route to us
              </button>
            </div>
          </template>
        </template>
        <template v-else>
          <div
            class="flex flex-col items-center justify-center h-full px-4 py-10"
          >
            <div class="font-medium text-center">
              Search for routes to get started...
            </div>
            <button
              @click.prevent="processFavoriteRoutes()"
              class="
                h-12
                px-4
                py-4
                mt-6
                font-bold
                text-center text-white
                rounded
                bg-active
              "
            >
              Use my favorite routes
            </button>
          </div>
        </template>
      </template>
    </div>
    <div
      class="
        w-full
        mx-2
        mt-6
        transform
        translate-y-6
        rounded-md
        lg:w-1/3 lg:ml-8 lg:mt-24 lg:mt-0 lg:w-1/4
      "
      :class="{'bg-gray-type-4 self-start': !fetchingFare}"
      v-if="data.first_route_id"
    >
      <payment-breakdown
        :loading-fare="fetchingFare"
        :payment-data="paymentData"
        :process-booking="book"
        :favourite-booking="is_favorite_trip"
        @departure-updated="updateFavoriteTripDeparture"
        @payment-success="handlePaymentSuccess"
      ></payment-breakdown>
    </div>
    <modal :show="showPaymentModal" @close="showPaymentModal = false">
      <div class="relative px-6 py-3 pb-6 text-dark-type-4">
        <div class>
          <div class="cursor-pointer" @click="showPaymentModal = false">
            <svg
              class="absolute"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
            >
              <g fill="none" fill-rule="evenodd">
                <circle cx="12" cy="12" r="12" fill="#FCFCFC" />
                <path
                  d="M16 8l-8 8m0-8l8 8"
                  stroke="#131615"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                />
              </g>
            </svg>
          </div>
          <div class="mx-auto my-4 font-bold text-center text-dark-type-3">
            Limited availability route
          </div>
          <div>
            <p>
              This route has limited availability, if you wish to have access to
              it please
              <a href="mailto:info@shuttlers.ng" class="text-active"
                >contact support</a
              >
            </p>
          </div>
        </div>
        <button
          @click="showPaymentModal = false"
          class="
            block
            w-full
            h-12
            py-4
            mt-8
            font-bold
            text-center text-white
            rounded
            focus:outline-none
            bg-active
          "
        >
          Dismiss
        </button>
      </div>
    </modal>

    <modal :show="showComingSoonModal" @close="showComingSoonModal = false">
      <route-notification
        @close="showComingSoonModal = false"
        @schedule-booking="scheduleBooking($event)"
        @customer-notified="resetSelectedOptions"
        :title="routeAvailabilityTitle"
        :content="routeAvailabilityText"
        :route-details="currentSelectedRouteData"
      >
      </route-notification>
    </modal>

    <modal
      :show="showRouteAvailabilityModal"
      @close="showRouteAvailabilityModal = false"
    >
      <route-availability
        @close="showRouteAvailabilityModal = false"
        :title="routeAvailabilityTitle"
        :content="routeAvailabilityText"
      >
      </route-availability>
    </modal>

    <modal
      :show="showRouteSuggestionModal"
      @close="showRouteSuggestionModal = false"
    >
      <div class="relative px-6 py-3 pb-6 text-dark-type-4">
        <div class>
          <div class="cursor-pointer" @click="showRouteSuggestionModal = false">
            <svg
              class="absolute"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
            >
              <g fill="none" fill-rule="evenodd">
                <circle cx="12" cy="12" r="12" fill="#FCFCFC" />
                <path
                  d="M16 8l-8 8m0-8l8 8"
                  stroke="#131615"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                />
              </g>
            </svg>
          </div>
          <div class="mx-auto my-4 font-bold text-center text-dark-type-3">
            Suggest Route
          </div>
          <div>
            <div class="mb-4">
              <p class="text-xs font-medium text-center text-dark-type-4">
                Suggest a route by filling the form below and our team will get
                right to it.
              </p>
            </div>
            <div class="flex mb-4">
              <div class="w-1/2 pr-3">
                <input
                  type="text"
                  v-model="suggestRoute.pickupBusStop"
                  placeholder="Pick up bus stop "
                  class="
                    w-full
                    h-12
                    px-3
                    py-4
                    text-xs
                    font-medium
                    bg-white
                    border
                    rounded-md
                    outline-none
                    placeholder-label-type-1
                    focus:outline-none
                  "
                />
              </div>

              <div class="w-1/2 pl-3">
                <input
                  type="text"
                  v-model="suggestRoute.destinationBusStop"
                  placeholder="Destination Bus Stop"
                  class="
                    w-full
                    h-12
                    px-3
                    py-4
                    pl-3
                    text-xs
                    font-medium
                    bg-white
                    border
                    rounded-md
                    outline-none
                    placeholder-label-type-1
                    focus:outline-none
                  "
                />
              </div>
            </div>

            <div class="flex">
              <div>
                <label class="w-full h-12 text-xs font-medium" for="">
                  Select departure time
                </label>
                <select
                  v-model="suggestRoute.departureTime"
                  class="
                    w-full
                    h-12
                    py-4
                    text-xs
                    font-medium
                    border
                    rounded-md
                    select_style
                  "
                  id="departure_time"
                >
                  <option value disabled>Select departure time</option>
                  <option value="12:00 AM">12:00AM</option>
                  <option value="01:00 AM">01:00AM</option>
                  <option value="02:00 AM">02:00AM</option>
                  <option value="03:00 AM">03:00AM</option>
                  <option value="04:00 AM">04:00AM</option>
                  <option value="05:00 AM">05:00AM</option>
                  <option value="06:00 AM">06:00AM</option>
                  <option value="07:00 AM">07:00AM</option>
                  <option value="08:00 AM">08:00AM</option>
                  <option value="09:00 AM">09:00AM</option>
                  <option value="10:00 AM">10:00AM</option>
                  <option value="11:00 AM">11:00AM</option>
                  <option value="12:00 PM">12:00PM</option>
                  <option value="01:00 PM">01:00PM</option>
                  <option value="02:00 PM">02:00PM</option>
                  <option value="03:00 PM">03:00PM</option>
                  <option value="04:00 PM">04:00PM</option>
                  <option value="05:00 PM">05:00PM</option>
                  <option value="06:00 PM">06:00PM</option>
                  <option value="07:00 PM">07:00PM</option>
                  <option value="08:00 PM">08:00PM</option>
                  <option value="09:00 PM">09:00PM</option>
                  <option value="10:00 PM">10:00PM</option>
                  <option value="11:00 PM">11:00PM</option>
                </select>
              </div>
              <div class="w-1/2 pl-3">
                <label class="w-full h-12 text-xs font-medium" for=""
                  >Trip type</label
                >
                <select
                  v-model="suggestRoute.tripType"
                  class="
                    w-full
                    h-12
                    py-4
                    text-xs
                    font-medium
                    border
                    rounded-md
                    trip_select
                  "
                  name=""
                  id=""
                >
                  <option value="One Way Trip">One Way Trip</option>
                  <option value="With Return Trip">With Return Trip</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        <button
          @click.prevent="_processRouteSuggestion()"
          class="
            block
            w-full
            h-12
            py-4
            mt-8
            mb-3
            font-bold
            text-center text-white
            rounded
            focus:outline-none
            bg-active
          "
        >
          Suggest Route
        </button>
      </div>
    </modal>
  </div>
</template>

<script>
import Modal from "@/components/Modal";
import PaymentBreakdown from "@/components/PaymentBreakdown";
import RouteDiscovery from "@/components/discover-routes/RouteDiscovery";
import { mapGetters } from "vuex";
import moment from "moment";
import axios from "@/services/axios";
import RouteInformation from "@/components/RouteInformation.vue";
import routeValidator from "@/mixins/route-validator";
import RouteNotification from "@/components/route-availability/route-notification";
import RouteAvailability from "@/components/route-availability/route-availability";
import FeatureFlagService, {FEATURE_FLAGS} from "@/services/feature-flags";
import {extractErrorMessage} from "@/services/utils";
import {ValidationException} from "@/models/validation-exception";

export default {
  components: {
    RouteAvailability,
    RouteNotification,
    RouteDiscovery,
    PaymentBreakdown,
    Modal,
    RouteInformation,
  },
  mixins: [routeValidator],
  data() {
    return {
      localBookingState: {
        firstTripData: null,
        returnTripData: null,
        responseData: null,
      },
      suggestRoute: {
        pickupBusStop: "",
        destinationBusStop: "",
        departureTime: "12:00 AM",
        tripType: "One Way Trip",
      },
      showRouteSuggestionModal: false,
      futureRoute: null,
      showComingSoonModal: false,
      showRouteAvailabilityModal: false,
      routeAvailabilityTitle: "",
      routeAvailabilityText: "",
      showPaymentModal: false,
      is_favorite_trip: false,
      favorite_trip_departure: null,
      routes: {
        options: {},
        discoveredRoutes: {
          first_trip_routes: [],
          second_trip_routes: [],
        },
      },
      data: {
        first_route_id: "",
        second_route_id: "",
        first_route: {},
        second_route: {},
      },
      discovering: false,
      discovered: false,
      favtData: [],
      currentSelectedRouteData: null,
      currentActiveRoute: null,
      itineraryPriceMap: new Map(),
      fetchingFare: false,
    };
  },
  mounted() {
    this.fetchFavoriteRoutes();
  },
  beforeRouteUpdate(to, from, next) {
    const routeData = Object.fromEntries(new URLSearchParams(to.query.options));
    if (routeData.isScheduleBooking === "true") {
      this.isScheduleBooking = true;
    } else {
      this.isScheduleBooking = false;
    }
    next();
  },
  computed: {
    ...mapGetters("auth", ["user"]),
    isNewDRPEnabled() {
      return FeatureFlagService.isFlagEnabled(FEATURE_FLAGS.DRP_CLIENT);
    },
    paymentData() {
      return {
        ...this.data,
        total: this.paymentTotal,
        ticketType: this.ticketType,
        pricePerPassenger: this.pricePerPassenger,
        passengers: 1,
        options: this.routes.options,
        route_details: {
          ...this.routes,
          luggage_quantity: this.routes.luggage_quantity,
        },
      };
    },
    ticketType() {
      return this.$options.filters.titleCase(this.routes.options.route_type);
    },
    isSubscription() {
      return this.routes.options.route_type === "subscription";
    },
    pricePerPassenger() {
      return this.data.first_route.fare || 0;
    },
    paymentTotal() {
      const firstRouteFare = this.data.first_route.fare || 0;
      const secondRouteFare = (this.isReturnTrip ? this.data.second_route.fare : 0) || 0;
      const subTotal = firstRouteFare + secondRouteFare;
      console.log({subTotal})
      let total = subTotal;
      if (this.isSubscription) {
        total =
          subTotal *
          this.routes.options.trip_days.length *
          this.routes.options.subscription_duration;
      }
      return total;
    },
    isReturnTrip() {
      return (
        this.routes.options.route_type === "return" ||
        this.routes.options.subscription_route_type === "return"
      );
    },
  },
  methods: {
    async getTripFare(itinerary) {
      const startStopData = itinerary.origin_bus_stop;
      const endStopData = itinerary.destination_bus_stop;
      let tripFare = null; //itinerary.fare;
      const key = `${itinerary.itinerary_id}_${startStopData.busStopId}_${endStopData.busStopId}`;
      if(this.itineraryPriceMap.has(key)){
        tripFare = this.itineraryPriceMap.get(key);
        return tripFare;
      }
      this.fetchingFare = true;
      try {
        const payload = {
          itineraryId: itinerary.itinerary_id,
          coordinates: [
            this.convertToLatLng(startStopData.coordinates),
            this.convertToLatLng(endStopData.coordinates)
          ],
          startStopId: startStopData.busStopId,
          endStopId: endStopData.busStopId,
        };
        const request = this.$http({url: `/drp/v1/pricing/pricing-calculator`, method: 'POST',
          data: payload, baseURL: process.env.VUE_APP_API_BASE_URL})
        const res = await request;
        const priceData = res.data;
        if(priceData.data) {
          this.itineraryPriceMap
            .set(`${priceData.data.itinerary_id}_${startStopData.busStopId}_${endStopData.busStopId}`, parseFloat(priceData.data.fare));
          tripFare = parseFloat(priceData.data.fare);
        }
      } catch (e) {
        this.$toast.error(extractErrorMessage(e, 'Unable to fetch trip fare'));
        console.error(e, 'Unable to get price');
      } finally {
        this.fetchingFare = false;
      }
      return tripFare;
    },

    arrivalTimeFormatted({ route_duration, departure_time, time_of_day }) {
      let routeDuration = route_duration;
      let departureTime = departure_time;
      let timeOfDay = time_of_day;

      let durationParts = routeDuration.split(" ");

      let duration = parseInt(durationParts[0]);

      if (durationParts[1] === "hour") {
        duration = duration * 60;
      }

      return moment(`${departureTime} ${timeOfDay}`, "h:mm A")
        .add(duration, "m")
        .format("h:mm a");
    },
    shouldUserAccessRoute(route) {
      return (
        route.route_visibility === "public" ||
        (route.route_visibility === "private" &&
          route.corporate_id &&
          (this.user ? route.corporate_id === this.user.corporate_id : true))
      );
    },
    async setRoutes(routes) {
      const trackingData = {
        source: routes.options.source_route.label,
        source_route_id: routes.options.source_route.id,
        destination: routes.options.destination_route.label,
        destination_route_id: routes.options.destination_route.id,
        first_trip_routes: [...routes.discoveredRoutes.first_trip_routes],
        second_trip_routes: [...routes.discoveredRoutes.second_trip_routes],
      };

      if (routes.discoveredRoutes.first_trip_routes.length) {
        this.$logEvent("search_booking_with_results", trackingData);
      } else {
        this.$logEvent("search_booking_without_results", trackingData);
      }

      this.routes = routes;

      this.discovering = false;
      this.discovered = true;
      this.is_favorite_trip = false;
    },
    validateSelectedRoute(route, returnTrip = false) {
      this.currentSelectedRouteData = {
        data: this.routes.options,
        minDate: returnTrip ? this.routes.options.departure_date : null,
        route,
      };
      let canProceed = true;
      const tripDate = returnTrip
        ? this.currentSelectedRouteData.data.return_date
        : this.currentSelectedRouteData.data.departure_date;
      this.validateRoute(
        route,
        {
          selectedDate: new Date(tripDate),
          tripType: this.currentSelectedRouteData.data.route_type,
          numberOfWeeks:
            this.currentSelectedRouteData.data.subscription_duration,
          selectedDays: this.currentSelectedRouteData.data.trip_days,
        },
        {
          availableInFuture: (routeData) => {
            this.routeAvailabilityTitle = `This route is coming soon`;
            this.routeAvailabilityText = `<div class="text-center"> <p>The selected route is only available from </p>
                                        <p class="font-bold">${
                                          routeData.route_availability_start_date
                                            ? new Date(
                                                routeData.route_availability_start_date
                                              ).toDateString()
                                            : "a date that will be communicated"
                                        }</p> </div>`;
            this.showComingSoonModal = true;
            canProceed = false;
          },
          notAvailable: (routeData, availableOnlyOnSelectDays) => {
            this.routeAvailabilityTitle = `Oops!`;
            this.routeAvailabilityText = `<div class="text-center"> <p>Sorry, this route is unavailable on the selected day. </p></div>`;
            if (availableOnlyOnSelectDays) {
              const selectedDays = route.route_availability_days
                ? JSON.parse(route.route_availability_days)
                : [];
              const selectedDaysString = selectedDays.join(", ");
              if (selectedDaysString) {
                this.routeAvailabilityText = `<div class="text-center"> <p>Sorry, this route is only available on the following days: </p>
                                        <p class="font-bold" style="text-transform: capitalize">${selectedDaysString}</p> </div>`;
              }
            }
            this.showRouteAvailabilityModal = true;
            canProceed = false;
          },
          available: () => {
            canProceed = true;
          },
        }
      );
      return canProceed;
    },
    async selectFirstRoute(route) {
      if (!this.shouldUserAccessRoute(route)) {
        this.showPaymentModal = true;
        return;
      }

      if (route.route_total_seats === 0) {
        this.$toast.error("There are no seats available for this route");
        return;
      }
      this.isScheduleBooking = false;
      this.currentActiveRoute = { ...route, isReturnTrip: false };
      if (!this.isScheduleBooking) {
        const canProceed = this.validateSelectedRoute(route);
        if (!canProceed) {
          return;
        }
      }
      if (!this.isReturnTrip) {
        this.scrollToTop();
      }
      if (this.is_favorite_trip) {
        this.data.is_favorite_trip = true;
      }
      this.data.first_route = route;
      this.data.first_route_id = route.route_id;
      if(this.isNewDRPEnabled) {
        const fare = await this.getTripFare(route);
        if(fare !== null) {
          route.fare = fare;
        } else {
          this.data.first_route = null;
          this.data.first_route_id = null;
          this.$toast.error("Could not fetch price for route itinerary");
        }
      }

    },
    async selectSecondRoute(route) {
      if (!this.shouldUserAccessRoute(route)) {
        this.showPaymentModal = true;
        return;
      }
      if (route.route_total_seats === 0) {
        return this.$toast.error("There are no seats available for this route");
      }
      this.isScheduleBooking = false;
      this.currentActiveRoute = {
        ...route,
        extras: { minDate: this.routes.options.departure_date },
        isReturnTrip: true,
      };
      if (!this.isScheduleBooking) {
        const canProceed = this.validateSelectedRoute(route, true);
        if (!canProceed) {
          return;
        }
      }
      this.data.second_route = route;
      this.data.second_route_id = route.route_id;
      this.scrollToTop();
      if(this.isNewDRPEnabled) {
        const fare = await this.getTripFare(route);
        if(fare !== null) {
          route.fare = fare;
        } else {
          this.data.second_route = null;
          this.data.second_route_id = null;
          this.$toast.error("Could not fetch price for route itinerary");
          return;
        }
      }

    },

    book(paymentInformation = null) {
      let routes = [
        {
          ...this.data.first_route,
          departure_date: this.routes.options.departure_date,
          luggage_quantity: this.routes.luggage_quantity,
        },
      ];

      if (this.routes.options.route_type === "return") {
        routes.push({
          ...this.data.second_route,
          departure_date: this.routes.options.return_date,
        });
      }

      if (this.data.is_favorite_trip) {
        this.routes.options = {
          departure_date: moment(new Date()).format("YYYY-MM-DD"),
          destination_route: this.data.first_route.to_bus_stop,
          return_date: moment(new Date()).format("YYYY-MM-DD"),
          route_type: "one-way",
          source_route: this.data.first_route.from_bus_stop,
          subscription_duration: "",
          subscription_route_type: "",
          trip_days: [new Date().getDay() + 1],
        };
        const getFormattedDate = (date) => {
          return moment(date).format("YYYY-MM-DD");
        };
        routes.map(
          (x) =>
            (x.departure_date = getFormattedDate(
              this.favorite_trip_departure || new Date()
            ))
        );
      }

      if (
        this.routes.options.route_type === "subscription" &&
        Object.prototype.hasOwnProperty.call(this.data.second_route, "route_id")
      ) {
        routes.push({
          ...this.data.second_route,
          departure_date: this.routes.options.departure_date,
        });
      }

      //Check seats availability for first and second trips
      const firstSeatUnavailable = this.data.first_route.route_total_seats < 1 &&
        !this.data.second_route.route_total_seats;
      const secondSeatUnavailable = this.data.first_route.route_total_seats < 1 &&
        Object.prototype.hasOwnProperty.call(this.data.second_route, "route_total_seats") &&
        this.data.second_route.route_total_seats < 1;
      const onlySecondAvailable = this.data.first_route.route_total_seats < 1 &&
        Object.prototype.hasOwnProperty.call(this.data.second_route, "route_total_seats") &&
        this.data.second_route.route_total_seats >= 1;

      if (firstSeatUnavailable) {
        throw new ValidationException("There are no seats available for this route");
      } else if (secondSeatUnavailable) {
        throw new ValidationException("There are no seats available for both routes");
      } else if (onlySecondAvailable) {
        if (
          window.confirm(
            "There are no seats for your first trip. You'll only be charged for the second trip. Click okay to proceed."
          )
        ) {
          routes.shift();
        } else {
          return location.reload();
        }
      }

      return Promise.all(
        routes.map((route) => this.bookRoute(route, paymentInformation))
      );
    },
    bookRoute(route, paymentInformation = null) {
      let payload = {
        route_id: route.route_id,
        itinerary_id: route.itinerary_id,
        pickup_id: route.pickup_id,
        destination_id: route.destination_id,
        start_date: route.departure_date,
        end_date: this.getEndDate(route),
        days_ids: this.getDays(route),
        recurring: !!this.isSubscription,
        meta: JSON.stringify(route),
        luggage_quantity: route.luggage_quantity,
        payment_source: paymentInformation
          ? paymentInformation.paymentSource
          : this.$store.state.app.walletSource || "main_balance",
        payment_reference: paymentInformation?.paymentReference,
      };
      const isNewDRPEnabled = FeatureFlagService.isFlagEnabled(FEATURE_FLAGS.DRP_CLIENT);
      if(isNewDRPEnabled) {
        payload = {...payload, differential_price: route.fare};
      }
      return this.$store.dispatch("app/bookRoute", payload);
    },
    processFavoriteRoutes() {
      this.discovered = true;
      this.is_favorite_trip = true;
      this.routes.discoveredRoutes.first_trip_routes = this.favtData;
      this.routes.favorite_trips = true;
    },
    getEndDate(route) {
      if (this.isSubscription) {
        let _date = this.$moment(route.departure_date).add(
          this.routes.options.subscription_duration,
          "weeks"
        );
        return _date.subtract(1, "days").format("YYYY-MM-DD");
      }

      return route.departure_date;
    },
    getDays(route) {
      if (this.isSubscription) {
        return this.routes.options.trip_days;
      }

      return [this.$moment(route.departure_date).day() + 1];
    },
    handlePaymentSuccess(bookings) {
      this.setBookingData(bookings);
      this.$router.push({
        name: "dashboard.booking-successful",
        params: { bookingId: bookings[0].id },
      });
    },
    processRouteSuggestion() {
      if (this.routes.options.source_route.label) {
        this.suggestRoute.pickupBusStop =
          this.routes.options.source_route.label;
      }

      if (this.routes.options.destination_route.label) {
        this.suggestRoute.destinationBusStop =
          this.routes.options.destination_route.label;
      }

      this.showRouteSuggestionModal = true;
    },
    async fetchFavoriteRoutes() {
      const userId = localStorage.getItem("__user_id");
      return axios
        .get(`/route-discovery/favorite-routes/${userId}`)
        .then((res) => {
          let data = res.data.data;
          this.favtData = data;
        })
        .catch(() => {
          return [];
        });
    },
    _processRouteSuggestion() {
      let suggestRoute = this.suggestRoute;
      let name;
      let email;
      let phone;

      if (this.user) {
        name = `${this.user.lname}, ${this.user.fname}`;
        email = this.user.email;
        phone = this.user.phone ? this.user.phone : null;
      }
      let timeData = suggestRoute.departureTime.split(" ");
      const data = {
        name: name ? name : "Guest",
        email,
        pickup: suggestRoute.pickupBusStop,
        destination: suggestRoute.destinationBusStop,
        departure_time: timeData[0],
        time_of_day: timeData[1],
        phone: phone,
        trip_type: suggestRoute.tripType,
      };

      return axios
        .post("suggest", data)
        .then(() => {
          return window.location.assign("/dashboard/request-successful");
        })
        .catch(() => {
          return window.location.assign("/dashboard/request-successful");
        });
    },
    updateFavoriteTripDeparture(data) {
      if (Array.isArray(data) && data.length) {
        this.favorite_trip_departure = data[0];
      }
    },
    setBookingData(bookings) {
      this.localBookingState.firstTripData = bookings[0];
      this.localBookingState.returnTripData =
        bookings.length > 1 ? bookings[1] : null;
      this.$store.dispatch(
        "app/updateLocalBookingData",
        this.localBookingState
      );
    },
    scheduleBooking(newDate, scheduleMode = true) {
      if (scheduleMode) {
        this.isScheduleBooking = true;
        if (this.is_favorite_trip) {
          this.data.is_favorite_trip = true;
        }

        const isReturnRoute = this.currentActiveRoute.isReturnTrip;
        if (!isReturnRoute) {
          this.data.first_route = this.currentActiveRoute;
          this.data.first_route_id = this.currentActiveRoute.route_id;
          if (newDate) {
            this.routes.options.departure_date = newDate;
          }
        } else {
          this.data.second_route = this.currentActiveRoute;
          this.data.second_route_id = this.currentActiveRoute.route_id;
          if (newDate) {
            this.routes.options.return_date = newDate;
          }
        }

        if (isReturnRoute || !this.isReturnTrip) {
          this.scrollToTop();
        }
      }
    },
    resetSelectedOptions() {
      this.isScheduleBooking = false;
      this.data = {
        first_route_id: "",
        second_route_id: "",
        first_route: {},
        second_route: {},
      };
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    convertToLatLng(coordinates) {
      return {latitude: coordinates[0], longitude: coordinates[1]};
    }
  },
};
</script>

<style scoped>
.lock-route {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  text-align: center;
  bottom: 0;
  margin: auto;
}

.overlay {
  height: 100%;
  width: 100%;
  background-color: red;
}

.lock-indicator {
  height: 100%;
  width: 100%;
  text-align: center;
  margin: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.2);
}

.select_style {
  width: 92%;
}

.trip_select {
  width: 105%;
  margin-left: -5%;
}

._info {
  display: flex;
  flex-flow: column;
}

._favt_routes {
  margin: -50% 0;
}

.disable-form {
  pointer-events: none;
  opacity: 0.7;
}
</style>
